import { useState } from "react";
import { AlertTriangle, Save, Trash2 } from "lucide-react";
import type {
  CalendarException,
  LineOperatingCalendar,
  RecurringException,
  ShiftsMode,
} from "@opus-dt/shared-types";
import { Button } from "@/components/ui/button";
import { Notify, toast } from "@/components/ui/notify";
import { ShiftsEditor } from "@/features/calendar/ShiftsEditor";
import { ExceptionsList } from "@/features/calendar/ExceptionsList";
import { fromDraft, toDraft } from "@/features/calendar/calendar-draft";
import type { ShiftForm } from "@/features/calendar/time-helpers";
import { RecurrenceSection } from "@/features/organization/RecurrenceSection";
import { useOrganizationCalendar } from "@/features/organization/hooks";
import { ShiftsModeChoice } from "./ShiftsModeChoice";
import { useDeleteLineCalendar, useUpdateLineCalendar } from "./hooks";

interface Props {
  lineId: string;
  initial: LineOperatingCalendar | null;
  onDone: () => void;
}

// Corpo do editor de expediente (dentro do SlideOver). Rascunho local; validação
// pelo schema compartilhado (fromDraft); salva via PUT, limpa via DELETE.
// A linha passou a poder declarar só a DIFERENÇA: ela segue o padrão da fábrica
// (INHERIT) ou declara o próprio (OVERRIDE), e em qualquer um dos dois pode ter exceções e regras
// recorrentes próprias. Herdar não é camisa de força.
export function CalendarSheetBody({ lineId, initial, onDone }: Props) {
  const seed = toDraft(initial);
  const [shifts, setShifts] = useState<ShiftForm[]>(seed.shifts);
  const [exceptions, setExceptions] = useState<CalendarException[]>(seed.exceptions);
  const [recurring, setRecurring] = useState<RecurringException[]>(
    initial?.recurringExceptions ?? []
  );
  const [mode, setMode] = useState<ShiftsMode>(initial?.shiftsMode ?? "OVERRIDE");
  const update = useUpdateLineCalendar(lineId);
  const remove = useDeleteLineCalendar(lineId);
  // Leitura, não escrita: serve para MOSTRAR o que a linha herda quando escolhe seguir o padrão.
  const orgCalendar = useOrganizationCalendar();

  const { calendar, error } = fromDraft({ shifts, exceptions });
  const inherits = mode === "INHERIT";
  const isEmpty = shifts.length === 0 && exceptions.length === 0 && recurring.length === 0;
  // Exceções SEM turno semanal, com expediente próprio: o calendário
  // fica válido, mas resolve a linha como FECHADA em todo dia normal (só EXTRA_WINDOW abre).
  // O aviso só vale para OVERRIDE: herdando, os turnos vêm da fábrica.
  const closedExceptExtras = !inherits && shifts.length === 0 && exceptions.length > 0;

  async function save() {
    if (error) return;
    // Rascunho inteiramente vazio E em OVERRIDE: se havia calendário, limpar; senão, nada a fazer.
    // Herdando, o registro precisa existir mesmo vazio — é ele que carrega a escolha.
    if (!calendar && recurring.length === 0 && !inherits) {
      if (initial) await clearCalendar();
      else onDone();
      return;
    }
    await update.mutateAsync({
      weeklyShifts: calendar?.weeklyShifts ?? [],
      exceptions: calendar?.exceptions ?? [],
      recurringExceptions: recurring,
      shiftsMode: mode,
    });
    toast.success("Expediente salvo.");
    onDone();
  }

  async function clearCalendar() {
    await remove.mutateAsync();
    toast.success("Expediente removido — a linha volta a seguir o padrão da fábrica.");
    onDone();
  }

  async function confirmClear() {
    const ok = await Notify.confirm({
      variant: "danger",
      title: "Remover expediente",
      description:
        "Os turnos, exceções e regras desta linha serão apagados. Ela passa a seguir o expediente da fábrica — ou, se a fábrica não tiver um, a derivar o tempo planejado das ordens.",
      confirmLabel: "Remover",
    });
    if (ok) await clearCalendar();
  }

  const busy = update.isPending || remove.isPending;

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 space-y-5 overflow-y-auto p-4">
        <ShiftsModeChoice
          value={mode}
          onChange={setMode}
          orgShifts={orgCalendar.data?.calendar?.weeklyShifts ?? null}
          disabled={busy}
        />

        {/* Herdando, o editor de turnos some em vez de ficar desabilitado: turnos guardados que
            não valem, exibidos ao lado de uma escolha que diz que não valem, é convite a editá-los
            achando que fazem efeito. Eles continuam no banco e voltam ao trocar de modo. */}
        {inherits ? null : (
          <ShiftsEditor
            value={shifts}
            onChange={setShifts}
            disabled={busy}
            emptyHint="Sem turnos, a linha fica fechada nos dias normais. Para seguir a fábrica, escolha a opção acima."
          />
        )}

        <ExceptionsList value={exceptions} onChange={setExceptions} disabled={busy} />
        <RecurrenceSection
          value={recurring}
          onChange={setRecurring}
          disabled={busy}
          variant="inline"
        />

        {error ? (
          <p
            className="flex items-center gap-1.5 rounded-md bg-rd-danger-50 px-3 py-2 text-[12px] text-rd-danger-600"
            role="alert"
          >
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden /> {error}
          </p>
        ) : null}
        {!error && closedExceptExtras ? (
          <p
            className="flex items-center gap-1.5 rounded-md bg-rd-warning-50 px-3 py-2 text-[12px] text-rd-warning-700"
            role="status"
          >
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden /> Sem turnos semanais, a
            linha fica <strong>fechada</strong> nos dias normais — só as janelas extras abrem.
            Adicione turnos ou escolha seguir o padrão da fábrica.
          </p>
        ) : null}
      </div>

      <div className="flex items-center gap-2 border-t border-border-subtle px-4 py-3">
        <Button type="button" onClick={() => void save()} disabled={busy || !!error}>
          <Save className="mr-1.5 h-4 w-4" aria-hidden /> Salvar
        </Button>
        {initial ? (
          <Button type="button" variant="ghost" onClick={() => void confirmClear()} disabled={busy}>
            <Trash2 className="mr-1.5 h-4 w-4" aria-hidden /> Remover expediente
          </Button>
        ) : null}
        {isEmpty && !inherits ? (
          <span className="ml-auto text-[11px] text-text-tertiary">
            Sem turnos ⇒ linha fechada nos dias normais.
          </span>
        ) : null}
      </div>
    </div>
  );
}
